import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { AlertTriangle, Loader2, Mail, RefreshCw, ShieldAlert, UserMinus } from 'lucide-react'
import {
  getLifecycleSettings, listLapsedUsers, removeLapsedUser, sendBulkReminders,
  sendLapsedReminder, sendRemovalNotice, setProjectStatus, updateLifecycleSettings,
  userProjects, type LapsedUser, type LifecycleSettings,
} from '@/api/billingAdmin'
import { AdminModal, Field, InfoBox, Td, Th, chipStyle, errText, tableStyle } from '../adminUi'

/**
 * Lifecycle configuration and the lapsed-user workflow.
 *
 * The workflow is deliberately ordered — reminder, then removal notice, then
 * removal — and the server enforces that order (removal 409s until a notice has
 * stood for the configured period). "Remove" never deletes: it downgrades to
 * Basic and deactivates applications beyond the Basic allowance, so a renewal
 * restores everything.
 */
export default function SubscriptionsPanel() {
  const [settings, setSettings] = useState<LifecycleSettings | null>(null)
  const [users, setUsers] = useState<LapsedUser[]>([])
  const [lapsedDays, setLapsedDays] = useState(0)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [busyUser, setBusyUser] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<LapsedUser | null>(null)
  const [drilldown, setDrilldown] = useState<LapsedUser | null>(null)

  const loadUsers = useCallback(async (days: number) => {
    try {
      const data = await listLapsedUsers(days)
      setUsers(data.users)
      setSettings(data.settings)
    } catch (e) {
      toast.error(errText(e, 'Could not load lapsed users'))
    }
  }, [])

  useEffect(() => {
    Promise.all([getLifecycleSettings(), listLapsedUsers(0)])
      .then(([s, u]) => { setSettings(s); setUsers(u.users) })
      .catch((e) => toast.error(errText(e, 'Could not load lifecycle settings')))
      .finally(() => setLoading(false))
  }, [])

  const save = async (patch: Partial<LifecycleSettings>) => {
    setSaving(true)
    try {
      setSettings(await updateLifecycleSettings(patch))
      toast.success('Saved')
    } catch (e) {
      toast.error(errText(e, 'Could not save'))
    } finally {
      setSaving(false)
    }
  }

  const remind = async (user: LapsedUser) => {
    setBusyUser(user.user_id)
    try {
      const res = await sendLapsedReminder(user.user_id)
      toast[res.sent ? 'success' : 'error'](res.sent ? 'Reminder sent' : 'The email could not be delivered')
      await loadUsers(lapsedDays)
    } catch (e) {
      toast.error(errText(e, 'Could not send the reminder'))
    } finally {
      setBusyUser(null)
    }
  }

  const notice = async (user: LapsedUser) => {
    if (!window.confirm(
      `Send a removal notice to ${user.email}?\n\nThis starts the ${user.removal_notice_days}-day ` +
      'clock after which their account can be downgraded and their applications deactivated.',
    )) return
    setBusyUser(user.user_id)
    try {
      await sendRemovalNotice(user.user_id)
      toast.success('Removal notice sent')
      await loadUsers(lapsedDays)
    } catch (e) {
      toast.error(errText(e, 'Could not send the notice'))
    } finally {
      setBusyUser(null)
    }
  }

  const doRemove = async (user: LapsedUser) => {
    setBusyUser(user.user_id)
    try {
      const res = await removeLapsedUser(user.user_id)
      toast.success(
        res.projects_deactivated.length
          ? `Downgraded to Basic; ${res.projects_deactivated.length} application(s) deactivated`
          : 'Downgraded to Basic',
      )
      setConfirming(null)
      await loadUsers(lapsedDays)
    } catch (e) {
      toast.error(errText(e, 'Could not remove the user'))
    } finally {
      setBusyUser(null)
    }
  }

  if (loading || !settings) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}><Loader2 className="spin" /></div>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* ── The master switch ─────────────────────────────────────────── */}
      <div style={{
        display: 'flex', gap: '0.75rem', alignItems: 'flex-start',
        padding: '0.875rem', borderRadius: 6,
        background: 'var(--color-bg-secondary)',
        border: `1px solid ${settings.credit_billing_enabled ? 'var(--color-success, #2e9e5b)' : 'var(--color-border)'}`,
      }}>
        <input
          type="checkbox"
          checked={settings.credit_billing_enabled}
          onChange={(e) => save({ credit_billing_enabled: e.target.checked })}
          disabled={saving}
          style={{ marginTop: 3 }}
        />
        <div style={{ fontSize: '0.875rem' }}>
          <strong>Credit billing is {settings.credit_billing_enabled ? 'ON' : 'OFF'}</strong>
          <div style={{ color: 'var(--color-text-secondary)', marginTop: '0.25rem', lineHeight: 1.5 }}>
            While this is off, the engine only observes: AI usage is converted to
            credits and recorded so you can see what billing WOULD cost, but no
            wallet is debited, no plan limit blocks anything, and no runtime seat
            is charged. Turn it on when the catalog, prices and gateway are ready.
          </div>
        </div>
      </div>

      {/* ── Cadence & policy ──────────────────────────────────────────── */}
      <div>
        <h3 style={{ fontSize: '1.0625rem', marginBottom: '0.75rem' }}>Lifecycle settings</h3>
        <div style={{ display: 'grid', gap: '0.875rem', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
          <Field label="Expiry reminders" hint="Days before expiry, comma-separated.">
            <input
              defaultValue={settings.reminder_days_before}
              onBlur={(e) => {
                if (e.target.value !== settings.reminder_days_before) {
                  void save({ reminder_days_before: e.target.value })
                }
              }}
              style={{ width: '100%' }}
            />
          </Field>
          <Field label="Grace reminder every (days)">
            <input type="number" min={1} defaultValue={settings.grace_reminder_every_days}
                   onBlur={(e) => void save({ grace_reminder_every_days: parseInt(e.target.value, 10) || 3 })}
                   style={{ width: '100%' }} />
          </Field>
          <Field label="Removal notice period (days)"
                 hint="How long a notice must stand before removal is allowed.">
            <input type="number" min={0} defaultValue={settings.removal_notice_days}
                   onBlur={(e) => void save({ removal_notice_days: parseInt(e.target.value, 10) || 0 })}
                   style={{ width: '100%' }} />
          </Field>
          <Field label="When a subscription lapses"
                 hint="Downgrade applies every Basic limit automatically; Block keeps the plan but denies editing, downloads and runtime access.">
            <select value={settings.lapse_policy}
                    onChange={(e) => void save({ lapse_policy: e.target.value as 'downgrade' | 'block' })}
                    style={{ width: '100%' }}>
              <option value="downgrade">Downgrade to Basic</option>
              <option value="block">Block usage</option>
            </select>
          </Field>
          <Field label="Auto-renew new subscriptions">
            <select value={settings.auto_renew_default ? 'yes' : 'no'}
                    onChange={(e) => void save({ auto_renew_default: e.target.value === 'yes' })}
                    style={{ width: '100%' }}>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </Field>
        </div>
      </div>

      {/* ── Lapsed subscriptions ──────────────────────────────────────── */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <h3 style={{ fontSize: '1.0625rem', margin: 0 }}>Lapsed subscriptions</h3>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <label style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
              Lapsed more than
              <input type="number" min={0} value={lapsedDays}
                     onChange={(e) => setLapsedDays(parseInt(e.target.value, 10) || 0)}
                     style={{ width: 64, margin: '0 0.375rem' }} />
              days
            </label>
            <button className="btn btn-secondary" onClick={() => loadUsers(lapsedDays)}>
              <RefreshCw size={14} /> Apply
            </button>
            {selected.size > 0 && (
              <button className="btn btn-primary" onClick={async () => {
                try {
                  await sendBulkReminders([...selected])
                  toast.success(`Reminders sent to ${selected.size} user(s)`)
                  setSelected(new Set())
                  await loadUsers(lapsedDays)
                } catch (e) { toast.error(errText(e, 'Could not send reminders')) }
              }}>
                <Mail size={14} /> Remind {selected.size}
              </button>
            )}
          </div>
        </div>

        <InfoBox>
          The order matters and is enforced: send a reminder, then a removal
          notice, and only after {settings.removal_notice_days} days can the
          account be removed. <strong>Remove never deletes anything</strong> — it
          downgrades to Basic and deactivates applications beyond the Basic
          allowance (keeping the most recently updated one). Renewing restores it all.
        </InfoBox>

        <table style={tableStyle}>
          <thead>
            <tr>
              <Th />
              <Th>User</Th><Th>Plan</Th><Th>Expired</Th><Th right>Days</Th>
              <Th>Last reminder</Th><Th>Removal notice</Th><Th right>Credits</Th><Th />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id}>
                <Td>
                  <input
                    type="checkbox"
                    checked={selected.has(u.user_id)}
                    onChange={() => setSelected((s) => {
                      const next = new Set(s)
                      if (next.has(u.user_id)) next.delete(u.user_id); else next.add(u.user_id)
                      return next
                    })}
                  />
                </Td>
                <Td>
                  <button
                    className="btn btn-secondary"
                    style={{ padding: 0, background: 'none', border: 'none', textAlign: 'left' }}
                    title="View this user's applications"
                    onClick={() => setDrilldown(u)}
                  >
                    <div style={{ fontWeight: 500 }}>{u.user_name}</div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>{u.email}</div>
                  </button>
                </Td>
                <Td><span style={chipStyle}>{u.plan_label}</span></Td>
                <Td>{u.current_period_end ? new Date(u.current_period_end).toLocaleDateString() : '—'}</Td>
                <Td right>{u.days_lapsed ?? '—'}</Td>
                <Td>{u.last_reminder_at ? new Date(u.last_reminder_at).toLocaleDateString() : '—'}</Td>
                <Td>
                  {u.removal_notice_at ? (
                    <span style={{ color: u.can_remove ? 'var(--color-danger, #d64545)' : 'var(--color-warning, #f0a020)' }}>
                      {new Date(u.removal_notice_at).toLocaleDateString()}
                    </span>
                  ) : '—'}
                </Td>
                <Td right>{u.balance.toLocaleString()}</Td>
                <Td right>
                  <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }}>
                    <button className="btn btn-secondary" style={{ padding: '0.15rem 0.4rem' }}
                            title="Send a renewal reminder"
                            disabled={busyUser === u.user_id}
                            onClick={() => remind(u)}>
                      <Mail size={12} />
                    </button>
                    <button className="btn btn-secondary" style={{ padding: '0.15rem 0.4rem' }}
                            title="Send the removal notice — starts the countdown"
                            disabled={busyUser === u.user_id}
                            onClick={() => notice(u)}>
                      <ShieldAlert size={12} />
                    </button>
                    <button
                      className="btn btn-secondary"
                      style={{
                        padding: '0.15rem 0.4rem',
                        color: u.can_remove ? 'var(--color-danger, #d64545)' : undefined,
                        opacity: u.can_remove ? 1 : 0.45,
                      }}
                      title={u.can_remove
                        ? 'Downgrade to Basic and deactivate applications beyond the allowance'
                        : u.removal_notice_at
                          ? `Available ${u.removal_notice_days} days after the notice was sent`
                          : 'Send a removal notice first'}
                      disabled={!u.can_remove || busyUser === u.user_id}
                      onClick={() => setConfirming(u)}
                    >
                      <UserMinus size={12} />
                    </button>
                  </div>
                </Td>
              </tr>
            ))}
            {!users.length && (
              <tr>
                <Td style={{ color: 'var(--color-text-secondary)' }}>
                  No lapsed subscriptions{lapsedDays ? ` older than ${lapsedDays} days` : ''}.
                </Td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {confirming && (
        <AdminModal title={`Remove ${confirming.user_name}?`} onClose={() => setConfirming(null)}>
          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.875rem' }}>
            <AlertTriangle size={20} style={{ color: 'var(--color-danger, #d64545)', flexShrink: 0 }} />
            <div style={{ fontSize: '0.9375rem', lineHeight: 1.5 }}>
              The removal notice was sent on{' '}
              <strong>{confirming.removal_notice_at ? new Date(confirming.removal_notice_at).toLocaleDateString() : '—'}</strong>.
              <p style={{ marginTop: '0.5rem', color: 'var(--color-text-secondary)' }}>
                This will downgrade <strong>{confirming.email}</strong> to Basic and
                deactivate every application beyond the Basic allowance, keeping the
                most recently updated one active. Nothing is deleted — their data,
                applications and {confirming.balance.toLocaleString()} credits all
                remain, and renewing restores full access.
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={() => setConfirming(null)}>Cancel</button>
            <button className="btn btn-danger" disabled={busyUser === confirming.user_id}
                    onClick={() => doRemove(confirming)}>
              {busyUser === confirming.user_id ? <Loader2 size={14} className="spin" /> : 'Downgrade & deactivate'}
            </button>
          </div>
        </AdminModal>
      )}

      {drilldown && (
        <ProjectDrilldown user={drilldown} onClose={() => setDrilldown(null)} />
      )}
    </div>
  )
}

/** Per-project activate/deactivate for one user (the instance-admin kill switch). */
function ProjectDrilldown({ user, onClose }: { user: LapsedUser; onClose: () => void }) {
  const [rows, setRows] = useState<Array<{ project_id: string; updated_at: string | null; is_active: boolean }>>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setRows((await userProjects(user.user_id)).projects)
    } catch (e) {
      toast.error(errText(e, 'Could not load the applications'))
    } finally {
      setLoading(false)
    }
  }, [user.user_id])

  useEffect(() => { void load() }, [load])

  const toggle = async (projectId: string, isActive: boolean) => {
    setBusy(projectId)
    try {
      await setProjectStatus(user.user_id, projectId, isActive,
        isActive ? 'Reactivated by instance admin' : 'Deactivated by instance admin')
      await load()
    } catch (e) {
      toast.error(errText(e, 'Could not change the application status'))
    } finally {
      setBusy(null)
    }
  }

  return (
    <AdminModal title={`Applications — ${user.user_name}`} onClose={onClose}>
      {loading ? <Loader2 className="spin" /> : (
        <>
          <InfoBox>
            A deactivated application is closed to everyone — its users see a
            branded "unavailable" screen and it cannot be edited. Nothing is
            deleted; reactivating restores it immediately.
          </InfoBox>
          <table style={tableStyle}>
            <thead><tr><Th>Application</Th><Th>Last updated</Th><Th right>Active</Th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.project_id}>
                  <Td>{r.project_id}</Td>
                  <Td>{r.updated_at ? new Date(r.updated_at).toLocaleDateString() : '—'}</Td>
                  <Td right>
                    <input
                      type="checkbox"
                      checked={r.is_active}
                      disabled={busy === r.project_id}
                      onChange={() => toggle(r.project_id, !r.is_active)}
                    />
                  </Td>
                </tr>
              ))}
              {!rows.length && (
                <tr><Td style={{ color: 'var(--color-text-secondary)' }}>No applications.</Td></tr>
              )}
            </tbody>
          </table>
        </>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
        <button className="btn btn-secondary" onClick={onClose}>Close</button>
      </div>
    </AdminModal>
  )
}
